import { WireTransferValues, BeneficiaryTypes, LocationOptions } from './Types'
import { generateExternalTransferId, getCountryCode } from '../../utils/payloadHelpers'
import isEmpty from 'lodash.isempty'
import { CountryItem } from '../../reducers/types'
import { getFrequencyInterval } from '../../components/ACHTransferForm/utils/getFrequencyInterval'
import { convertToValidDate } from '../../utils'
import { getFrequencyUnit } from '../../components/ACHTransferForm/utils/getFrequencyUnit'
import { getFeeOverrideDetails } from '../../components/TransferFees/utils/getFeeOverrideDetails'
import { WirePayload } from '@Types'

enum DestinationCode {
  ABA = 'ABA',
  BIC = 'BIC'
}

const formatPayload = (
  {
    fromAccount,
    totalBalanceCheckbox,
    transferAmountRaw,
    beneficiaryAccountNumOrIBAN,
    beneficiaryType,
    bankRouting,
    bankDestination,
    bankName,
    bankCountry,
    bankCity,
    bankState,
    bankPostalCode,
    bankUseIntermediary,
    intermediaryAccountNumber,
    intermediaryName,
    intermediaryCity,
    intermediaryState,
    intermediaryCountry,
    intermediaryZip,
    intermediaryAddress,
    intermediaryAddressLine2,
    thirdPartyAddress,
    thirdPartyAddressLine2,
    thirdPartyCity,
    thirdPartyState,
    thirdPartyCountry,
    thirdPartyZip,
    beneficiaryName,
    distributionReason,
    federalTaxWithholdingAmountRaw,
    federalTaxWithholdingType,
    stateTaxWithholdingAmountRaw,
    stateTaxWithholdingType,
    receivingInstitutionName,
    transferRecurrence,
    transferStartDate,
    transferFrequency,
    transferScheduledDate,
    whenToSend,
    numberOfTransfers,
    fees,
    optOutFormProvided
  }: WireTransferValues,
  countryList: CountryItem[],
  isScheduledTransfer: boolean
): WirePayload => {
  return {
    externalId: generateExternalTransferId(),
    account: fromAccount,
    ...(!totalBalanceCheckbox && { amount: transferAmountRaw }),
    ...(totalBalanceCheckbox && { isFullDisbursement: true }),
    beneficiaryDetails: {
      account: beneficiaryAccountNumOrIBAN,
      isThirdParty: beneficiaryType === BeneficiaryTypes.ThirdParty,
      ...(beneficiaryType === BeneficiaryTypes.ThirdParty && {
        name: beneficiaryName,
        addressDetails: {
          streetAddress: isEmpty(thirdPartyAddressLine2)
            ? [thirdPartyAddress]
            : [thirdPartyAddress, thirdPartyAddressLine2],
          city: thirdPartyCity,
          country: getCountryCode(countryList, thirdPartyCountry),
          ...(thirdPartyState && { state: thirdPartyState }),
          ...(thirdPartyZip && { postalCode: thirdPartyZip })
        }
      })
    },
    recipientBankDetails: {
      identifierDetails: {
        id: bankRouting,
        type: bankDestination === LocationOptions.Domestic ? DestinationCode.ABA : DestinationCode.BIC
      },
      ...(bankDestination === LocationOptions.International && {
        country: getCountryCode(countryList, bankCountry),
        city: bankCity,
        name: bankName,
        ...(bankState && { stateProvince: bankState }),
        ...(bankPostalCode && { postalCode: bankPostalCode })
      })
    },
    ...(bankUseIntermediary && {
      intermediaryDetails: {
        account: intermediaryAccountNumber,
        name: intermediaryName,
        addressDetails: {
          streetAddress: isEmpty(intermediaryAddressLine2)
            ? [intermediaryAddress]
            : [intermediaryAddress, intermediaryAddressLine2],
          city: intermediaryCity,
          country: getCountryCode(countryList, intermediaryCountry),
          ...(intermediaryState && { state: intermediaryState }),
          ...(intermediaryZip && { postalCode: intermediaryZip })
        }
      }
    }),
    ...(distributionReason && {
      iraDistributionDetails: {
        distributionReason,
        ...(distributionReason === 'TRANSFER' && { receivingInstitutionName }),
        ...(federalTaxWithholdingType && {
          federalTaxWithholding: {
            valueType: federalTaxWithholdingType,
            value: federalTaxWithholdingAmountRaw
          }
        }),
        ...(stateTaxWithholdingType && {
          stateTaxWithholding: {
            valueType: stateTaxWithholdingType,
            value: stateTaxWithholdingAmountRaw
          }
        }),
        ...(optOutFormProvided && { hasStateWithholdingWaiver: optOutFormProvided })
      }
    }),
    ...(isScheduledTransfer && {
      ...(transferRecurrence === 'RECURRING' && {
        startDate: convertToValidDate(transferStartDate),
        frequencyUnit: getFrequencyUnit(transferFrequency),
        frequencyInterval: getFrequencyInterval(transferFrequency),
        ...(numberOfTransfers &&
          Number(numberOfTransfers) > 0 && {
            occurrences: Number(numberOfTransfers)
          })
      }),
      ...(transferRecurrence === 'ONE_TIME' && {
        startDate: convertToValidDate(transferScheduledDate),
        frequencyUnit: 'DAY',
        frequencyInterval: 1,
        ...(whenToSend === 'IN_THE_FUTURE' && { occurrences: 1 }),
        ...(whenToSend === 'IMMEDIATELY' &&
          numberOfTransfers &&
          Number(numberOfTransfers) > 0 && {
            occurrences: Number(numberOfTransfers)
          })
      })
    }),
    ...(!isEmpty(fees) && { feeOverrideDetails: getFeeOverrideDetails(fees) })
  }
}

export default formatPayload
